import { useCallback } from "react";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuIcon,
} from "@/components/ui/dropdown-menu";
import {
  NumericFilterMenuItemType,
  numericOperatorToLabel,
} from "../FilterMenu.model";
import { numericFilter, NumericOperator } from "../FieldFilter.model";
import { NumericBoundInput } from "../NumericBoundInput";

interface NumericFilterMenuItemProps<Field extends string>
  extends NumericFilterMenuItemType<Field> {
  onChange: (item: NumericFilterMenuItemType<Field>) => void;
  onRequestClose?: () => void;
}

export function NumericFilterMenuItem<Field extends string>({
  onChange,
  onRequestClose,
  ...item
}: NumericFilterMenuItemProps<Field>) {
  const handleAmountSubmit = useCallback(
    (operator: NumericOperator, value: number, closeOnSubmit: boolean) => {
      onChange({
        ...item,
        value: numericFilter(operator, value),
      });

      if (closeOnSubmit) {
        onRequestClose?.();
      }
    },
    [item, onChange, onRequestClose]
  );

  if (item.value != null) {
    const { operator, value } = item.value;

    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {item.icon && <DropdownMenuIcon>{item.icon}</DropdownMenuIcon>}
          {item.label}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          sideOffset={8}
          alignOffset={-5}
          className="w-[240px]"
        >
          <NumericBoundInput
            label={item.label}
            value={value}
            onSubmit={(nextValue) =>
              handleAmountSubmit(operator, nextValue, false)
            }
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {item.icon && <DropdownMenuIcon>{item.icon}</DropdownMenuIcon>}
        {item.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent sideOffset={8} alignOffset={-5}>
        {(["gte", "lte"] as const).map((operator) => (
          <DropdownMenuSub key={operator}>
            <DropdownMenuSubTrigger>
              {numericOperatorToLabel[operator]}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              sideOffset={8}
              alignOffset={-5}
              className="w-[240px]"
            >
              <NumericBoundInput
                label={item.label}
                onSubmit={(value) => handleAmountSubmit(operator, value, true)}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
